/**
 * Accountant Dashboard
 * Manage clients, view pending invitations, and track activity
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/lib/trpc";
import {
  Building2,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  Star,
  Settings,
  ExternalLink,
  Shield,
  Eye,
  Edit,
  Lock,
} from "lucide-react";
import { Link } from "wouter";

const ACCESS_LEVELS = [
  {
    value: "read_only",
    label: "Read Only",
    icon: Eye,
    description: "View transactions and reports",
  },
  {
    value: "categorize",
    label: "Categorize",
    icon: Edit,
    description: "View + modify categories",
  },
  {
    value: "full_access",
    label: "Full Access",
    icon: Lock,
    description: "All except delete company",
  },
];

export default function AccountantDashboard() {
  return (
    <DashboardLayout>
      <DashboardContent />
    </DashboardLayout>
  );
}

function DashboardContent() {
  const { user } = useAuth();
  const utils = trpc.useUtils();

  // Get accountant profile
  const { data: profile, isLoading: profileLoading } =
    trpc.accountant.myProfile.useQuery();

  // Get clients
  const { data: clientsData, isLoading: clientsLoading } =
    trpc.accountant.listClients.useQuery(undefined, { enabled: !!profile });

  // Get pending invitations
  const { data: invitationsData, isLoading: invitationsLoading } =
    trpc.accountant.pendingInvitations.useQuery(undefined, {
      enabled: !!profile,
    });

  // Accept invitation mutation
  const acceptMutation = trpc.accountant.acceptInvitation.useMutation({
    onSuccess: () => {
      utils.accountant.pendingInvitations.invalidate();
      utils.accountant.listClients.invalidate();
    },
  });

  // Decline invitation mutation
  const declineMutation = trpc.accountant.declineInvitation.useMutation({
    onSuccess: () => {
      utils.accountant.pendingInvitations.invalidate();
    },
  });

  if (profileLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded animate-pulse w-64" />
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-20 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <Building2 className="h-16 w-16 text-muted-foreground" />
        <h2 className="text-2xl font-bold">
          You're not registered as an accountant
        </h2>
        <p className="text-muted-foreground text-center max-w-md">
          Register as an accountant to manage clients, receive invitations, and
          appear in the marketplace.
        </p>
        <Link href="/accountant/register">
          <Button size="lg">Register as Accountant</Button>
        </Link>
      </div>
    );
  }

  const activeClients = clientsData || [];
  const pendingInvitations = invitationsData || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Accountant Dashboard
          </h1>
          <p className="text-muted-foreground">
            {profile.firmName} - Manage your clients and settings
          </p>
        </div>
        <div className="flex gap-2">
          <Link href={`/accountant/${profile.id}`}>
            <Button variant="outline">
              <ExternalLink className="mr-2 h-4 w-4" />
              View Public Profile
            </Button>
          </Link>
          <Link href="/accountant/settings">
            <Button variant="outline">
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Clients
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeClients.length}</div>
            <p className="text-xs text-muted-foreground">
              Companies you manage
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pending Invitations
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {pendingInvitations.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Awaiting your response
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Average Rating
            </CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {profile.avgRating && profile.avgRating > 0
                ? (profile.avgRating / 100).toFixed(1)
                : "N/A"}
            </div>
            <p className="text-xs text-muted-foreground">
              {profile.reviewCount || 0} reviews
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Profile Status
            </CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {profile.isVerified ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-[var(--success)]" />
                  <span className="font-medium">Verified</span>
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5 text-[var(--warning)]" />
                  <span className="font-medium">Pending</span>
                </>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {profile.isPublic ? "Listed in marketplace" : "Not public"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="clients" className="space-y-4">
        <TabsList>
          <TabsTrigger value="clients">
            Clients ({activeClients.length})
          </TabsTrigger>
          <TabsTrigger value="invitations">
            Invitations
            {pendingInvitations.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {pendingInvitations.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Clients Tab */}
        <TabsContent value="clients" className="space-y-4">
          {clientsLoading ? (
            <Card className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-40 bg-muted rounded" />
              </CardContent>
            </Card>
          ) : activeClients.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Your Clients</CardTitle>
                <CardDescription>
                  Companies you have access to manage
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Access Level</TableHead>
                      <TableHead>Since</TableHead>
                      <TableHead>Last Accessed</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeClients.map(client => (
                      <ClientRow key={client.relationshipId} client={client} />
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No clients yet</h3>
                <p className="text-sm text-muted-foreground text-center max-w-md">
                  When companies invite you to manage their accounts, they'll
                  appear here. Make sure your profile is public to be discovered
                  in the marketplace.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Invitations Tab */}
        <TabsContent value="invitations" className="space-y-4">
          {invitationsLoading ? (
            <Card className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-40 bg-muted rounded" />
              </CardContent>
            </Card>
          ) : pendingInvitations.length > 0 ? (
            <div className="grid gap-4">
              {pendingInvitations.map(invitation => (
                <InvitationCard
                  key={invitation.id}
                  invitation={invitation}
                  onAccept={() =>
                    acceptMutation.mutate({ invitationId: invitation.id })
                  }
                  onDecline={() =>
                    declineMutation.mutate({ invitationId: invitation.id })
                  }
                  isAccepting={acceptMutation.isPending}
                  isDeclining={declineMutation.isPending}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  No pending invitations
                </h3>
                <p className="text-sm text-muted-foreground text-center max-w-md">
                  When companies invite you to access their accounts, you'll see
                  them here.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface ClientRowProps {
  client: {
    relationshipId: number;
    companyId: number;
    companyName: string;
    companyUid: string | null;
    companyLegalForm: string | null;
    accessLevel: "read_only" | "categorize" | "full_access" | "admin";
    acceptedAt: Date | null;
    lastAccessedAt: Date | null;
  };
}

function ClientRow({ client }: ClientRowProps) {
  const accessConfig = ACCESS_LEVELS.find(l => l.value === client.accessLevel);
  const AccessIcon = accessConfig?.icon || Eye;

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="font-medium">{client.companyName}</div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <AccessIcon className="h-4 w-4 text-muted-foreground" />
          <span className="capitalize">
            {client.accessLevel.replace("_", " ")}
          </span>
        </div>
      </TableCell>
      <TableCell>
        {client.acceptedAt
          ? new Date(client.acceptedAt).toLocaleDateString()
          : "-"}
      </TableCell>
      <TableCell>
        {client.lastAccessedAt
          ? new Date(client.lastAccessedAt).toLocaleDateString()
          : "Never"}
      </TableCell>
      <TableCell className="text-right">
        <Link href={`/company/${client.companyId}/dashboard`}>
          <Button variant="outline" size="sm">
            Open
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </TableCell>
    </TableRow>
  );
}

interface InvitationCardProps {
  invitation: {
    id: number;
    companyId: number;
    companyName: string;
    companyUid: string | null;
    accessLevel: "read_only" | "categorize" | "full_access" | "admin";
    invitedAt: Date;
  };
  onAccept: () => void;
  onDecline: () => void;
  isAccepting: boolean;
  isDeclining: boolean;
}

function InvitationCard({
  invitation,
  onAccept,
  onDecline,
  isAccepting,
  isDeclining,
}: InvitationCardProps) {
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const accessConfig = ACCESS_LEVELS.find(
    l => l.value === invitation.accessLevel
  );
  const AccessIcon = accessConfig?.icon || Eye;

  return (
    <>
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  {invitation.companyName}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Invited on{" "}
                  {new Date(invitation.invitedAt).toLocaleDateString()}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="flex items-center gap-1">
                    <AccessIcon className="h-3 w-3" />
                    {accessConfig?.label || invitation.accessLevel}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {accessConfig?.description}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeclineDialog(true)}
                disabled={isAccepting || isDeclining}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Decline
              </Button>
              <Button onClick={onAccept} disabled={isAccepting || isDeclining}>
                {isAccepting ? (
                  "Accepting..."
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Accept
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline Invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to decline the invitation from{" "}
              {invitation.companyName}? They will be notified that you declined.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDecline();
                setShowDeclineDialog(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeclining ? "Declining..." : "Decline"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
